import { Box, Container, Grid, Skeleton, Stack, styled, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';

//Components
import PlanCard from 'src/features/Pricing/PlanCard';

// Utilities
import { useQueryListPlans, useQueryUsageInformation } from 'src/services/queries';
import { useQueryRuntimeConfig } from 'src/services/queries/useQueryRuntimeConfig';
import ImageComponent from 'src/components/common/Image';
import { ICON_BANNER_HOME_PAGE, ICON_CONFIRM_GET_TRIAL, ICON_WALLET_PRICING_PLAN } from 'src/utils/constants/imagesUrl';
import { SUCCESS_CODE } from 'src/utils/constants/statusCode';
import { isEmpty, map } from 'lodash';
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';
import { getCookie } from 'src/utils/helpers/cookies';
import { CAMPAIGN_PROGRAM_KEY } from 'src/utils/constants/config';
import SpecialPlanCard from 'src/features/Pricing/SpecialPlanCard';
import { SectionErrorBoundary } from 'src/components/ErrorBoundary/SectionErrorBoundary';
import EnterprisePlanCard from './EnterprisePlanCard';

const StyledContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(7),
}));

const Banner = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  width: '100%',
  background: ' linear-gradient(91deg, #422AFB 2.03%, #5451F8 98.7%)',
  borderRadius: theme.shape.borderRadius * 2,
  zIndex: 1000,
  position: 'relative',
  padding: theme.spacing(1.5),
  gap: theme.spacing(4),
}));

const Pricing = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const existedCampaignSlug = getCookie(CAMPAIGN_PROGRAM_KEY);
  const { data, isFetching } = useQueryListPlans(existedCampaignSlug);
  const { data: dataUsage } = useQueryUsageInformation();
  const isExistedPlan = dataUsage?.status_code === SUCCESS_CODE.GET;
  const { isFetching: isRuntimeConfigFetching } = useQueryRuntimeConfig();
  const campaignDetail = data?.data?.campaign;
  const campaignListPlan = getArrayDefault(campaignDetail?.plans);

  const listPlan = getArrayDefault(data?.data?.plans);

  return (
    <StyledContainer maxWidth="xl">
      <Box
        sx={{
          height: '100%',
          background: theme.palette.background.paper1,
        }}
      >
        {!isExistedPlan && (
          <Banner>
            <ImageComponent
              imgSrc={ICON_WALLET_PRICING_PLAN}
              style={{ objectFit: 'cover', display: 'inline-block' }}
              height={150}
            />
            <ImageComponent
              imgSrc={ICON_BANNER_HOME_PAGE}
              style={{ position: 'absolute', top: 0, right: 0 }}
              height="100%"
            />
            <Stack justifyContent="center" flex={4} gap={1}>
              <Typography variant="h5" color="white" align="left">
                Please Choose a Plan That Suits You
              </Typography>
              <Typography variant="body1" color="white" align="left">
                Unlock unlimited features and data access with flexible credits—use them as you need.
              </Typography>
            </Stack>
          </Banner>
        )}
        <Box mt={3}>
          <Typography variant="h4">{t('pricing.title')}</Typography>
          <Typography variant="body1" color="text.secondary">
            {t('pricing.sub_title')}
          </Typography>
        </Box>
        <Box mt={1} />
        <SectionErrorBoundary resetKey="PricingSpecialOffers" sectionName="PricingSpecialOffers">
          {!isEmpty(campaignListPlan) && (
            <Box mt={3}>
              <Typography variant="h5">
                <ImageComponent imgSrc={ICON_CONFIRM_GET_TRIAL} height={80} /> Special Offers
              </Typography>
              <Grid container spacing={3} pt={3}>
                {isFetching || isRuntimeConfigFetching
                  ? new Array(3).fill(0).map((_, index) => (
                      <Grid key={`plan-detail-${index}-loading`} item sm={12} md={6} lg={4} xl={3}>
                        <Skeleton variant="rectangular" animation="wave" height={150} />
                      </Grid>
                    ))
                  : map(campaignListPlan, (planDetail, index) => (
                      <Grid key={`plan-detail-${index}-${planDetail.id}`} item sm={12} md={6} lg={4} xl={3}>
                        <SpecialPlanCard
                          planDetail={planDetail}
                          dataUsage={dataUsage}
                          campaignSlug={existedCampaignSlug || ''}
                        />
                      </Grid>
                    ))}
              </Grid>
              <Typography variant="h5" mt={3}>
                Plans
              </Typography>
            </Box>
          )}
        </SectionErrorBoundary>
        <SectionErrorBoundary resetKey="PricingAllPlans" sectionName="PricingAllPlans">
          <Grid container spacing={3} pt={3}>
            {isFetching || isRuntimeConfigFetching ? (
              new Array(4).fill(0).map((_, index) => (
                <Grid key={`plan-detail-${index}-loading`} item sm={12} md={6} lg={4} xl={3}>
                  <Skeleton variant="rectangular" animation="wave" height={150} />
                </Grid>
              ))
            ) : (
              <>
                {map(listPlan, (planDetail, index) => (
                  <Grid key={`plan-detail-${index}-${planDetail.id}`} item sm={12} md={6} lg={4} xl={3}>
                    <PlanCard planDetail={planDetail} dataUsage={dataUsage} />
                  </Grid>
                ))}
                <Grid item sm={12} md={6} lg={4} xl={3}>
                  <EnterprisePlanCard />
                </Grid>
              </>
            )}
          </Grid>
        </SectionErrorBoundary>
      </Box>
    </StyledContainer>
  );
};

export default Pricing;
